/**
 * Iter106 — Master Console: Claire Insights.
 *
 * Consumes GET /api/claire/insights and renders:
 *   • KPIs (total sessions, kb hit rate, uncertain rate, events count)
 *   • Per-route table (chats / uncertain / kb_used)
 *   • Top user questions
 *   • Event counts (help_button_click, whatsapp_escalation, …)
 *
 * Purpose: close the learning loop — quickly see WHERE users get stuck
 * (high uncertain count by route) and which KB topics need expansion.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2, MessageCircle, AlertTriangle, BookOpen, MousePointerClick,
  Sparkles, ExternalLink, Phone,
} from "lucide-react";
import { toast } from "sonner";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function KPI({ icon: Icon, label, value, hint, accent = "indigo" }) {
  const accents = {
    indigo: "bg-indigo-50 text-indigo-700",
    amber:  "bg-amber-50 text-amber-700",
    emerald:"bg-emerald-50 text-emerald-700",
    rose:   "bg-rose-50 text-rose-700",
  };
  return (
    <div
      data-testid={`claire-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="al-card p-4 flex items-center gap-3"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accents[accent]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
        <div className="text-[20px] font-black text-[#111827] leading-tight">{value}</div>
        {hint && <div className="text-[10.5px] text-[#9CA3AF] mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

export default function MasterClaireInsights() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data: payload } = await axios.get(`${API}/claire/insights?days=${d}`);
      setData(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar insights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const byRoute = data?.by_route || [];
  const topQ    = data?.top_questions || [];
  const events  = data?.events || [];

  // Aggregate KPIs from per-route rows.
  const totalChats     = byRoute.reduce((s, r) => s + (r.total || 0), 0);
  const totalUncertain = byRoute.reduce((s, r) => s + (r.uncertain || 0), 0);
  const totalKbUsed    = byRoute.reduce((s, r) => s + (r.kb_used || 0), 0);
  const eventTotal     = events.reduce((s, e) => s + (e.count || 0), 0);
  const helpClicks     = (events.find((e) => e._id === "help_button_click") || {}).count || 0;
  const escalations    = (events.find((e) => e._id === "whatsapp_escalation") || {}).count || 0;

  const pct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : "—");
  const fmtRoute = (r) => r === null || r === undefined ? "—" : r;

  return (
    <MasterLayout title="Claire Insights">
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="master-claire-insights">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B7280]">
              Master Console
            </p>
            <h1 className="font-display text-[28px] font-black text-[#111827] tracking-tight inline-flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#7C3AED]" />
              Claire Insights
            </h1>
            <p className="text-[13px] text-[#6B7280] mt-1">
              Onde os usuários travam, quais perguntas se repetem e onde a KB
              precisa crescer — janela últimos {days} dias.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="claire-insights-window"
              className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px]"
            >
              <option value={7}>Últimos 7 dias</option>
              <option value={14}>Últimos 14 dias</option>
              <option value={30}>Últimos 30 dias</option>
              <option value={90}>Últimos 90 dias</option>
            </select>
            <a
              href="/master/claire-kb"
              data-testid="claire-insights-goto-kb"
              className="h-10 px-3 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] text-white text-[13px] font-bold inline-flex items-center gap-1.5 hover:from-[#6D28D9] hover:to-[#4338CA]"
            >
              <BookOpen className="w-4 h-4" /> Editar KB
            </a>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-[#6B7280]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando…
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <KPI icon={MessageCircle} label="Conversas" value={totalChats}
                   hint="Total de turns recebidos" accent="indigo" />
              <KPI icon={AlertTriangle} label="Incertas" value={`${totalUncertain} · ${pct(totalUncertain, totalChats)}`}
                   hint="Claire disse 'não sei'" accent="amber" />
              <KPI icon={BookOpen} label="KB ativada" value={`${totalKbUsed} · ${pct(totalKbUsed, totalChats)}`}
                   hint="Resposta usou base de conhecimento" accent="emerald" />
              <KPI icon={MousePointerClick} label="Cliques + Escalations"
                   value={`${helpClicks} / ${escalations}`}
                   hint="Help-button · WhatsApp human" accent="rose" />
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
              {/* Per-route breakdown */}
              <section className="al-card overflow-hidden" data-testid="claire-by-route">
                <header className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
                  <h2 className="text-[13px] font-bold text-[#111827]">Por rota</h2>
                  <span className="text-[10.5px] text-[#6B7280]">
                    Onde os usuários conversam (rótulo = `module_path` enviado pelo widget)
                  </span>
                </header>
                <table className="w-full text-[12.5px]">
                  <thead className="bg-[#F9FAFB] text-[10.5px] uppercase tracking-wider text-[#6B7280]">
                    <tr>
                      <th className="text-left px-3 py-2">Rota</th>
                      <th className="text-right px-3 py-2">Chats</th>
                      <th className="text-right px-3 py-2">Incertas</th>
                      <th className="text-right px-3 py-2">KB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byRoute.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-6 text-center text-[#9CA3AF] italic">
                        Sem dados na janela.
                      </td></tr>
                    )}
                    {byRoute.map((r) => {
                      const route = fmtRoute(r._id);
                      const uncertainShare = r.total > 0 ? r.uncertain / r.total : 0;
                      return (
                        <tr key={String(r._id)} className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB]">
                          <td className="px-3 py-2 font-mono text-[12px] text-[#111827]">
                            {route}
                          </td>
                          <td className="px-3 py-2 text-right font-bold">{r.total}</td>
                          <td className={`px-3 py-2 text-right ${
                            uncertainShare >= 0.3 ? "text-rose-600 font-bold" :
                            uncertainShare >= 0.15 ? "text-amber-600 font-semibold" :
                            "text-[#374151]"
                          }`}>
                            {r.uncertain} <span className="text-[10px] text-[#9CA3AF]">({pct(r.uncertain, r.total)})</span>
                          </td>
                          <td className="px-3 py-2 text-right text-[#374151]">
                            {r.kb_used} <span className="text-[10px] text-[#9CA3AF]">({pct(r.kb_used, r.total)})</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>

              {/* Top user questions */}
              <section className="al-card overflow-hidden" data-testid="claire-top-questions">
                <header className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
                  <h2 className="text-[13px] font-bold text-[#111827]">Top perguntas</h2>
                  <span className="text-[10.5px] text-[#6B7280]">
                    O que mais perguntam (frequência exata)
                  </span>
                </header>
                <ol className="divide-y divide-[#F3F4F6]">
                  {topQ.length === 0 && (
                    <li className="px-4 py-6 text-center text-[#9CA3AF] italic text-[12.5px]">
                      Sem dados na janela.
                    </li>
                  )}
                  {topQ.map((q, idx) => (
                    <li key={`${q._id || "blank"}-${idx}`}
                        className="px-4 py-2 flex items-start gap-3 hover:bg-[#F9FAFB]">
                      <span className="text-[11px] font-bold text-[#7C3AED] w-6 shrink-0 mt-0.5">#{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] text-[#111827] leading-snug line-clamp-2">
                          {q._id || <span className="italic text-[#9CA3AF]">(vazia)</span>}
                        </div>
                        <div className="text-[10.5px] text-[#9CA3AF] mt-0.5">
                          {q.count} vez{q.count > 1 ? "es" : ""}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>

              {/* Events */}
              <section className="al-card overflow-hidden lg:col-span-2" data-testid="claire-events">
                <header className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
                  <h2 className="text-[13px] font-bold text-[#111827]">Eventos</h2>
                  <span className="text-[10.5px] text-[#6B7280]">
                    Total: {eventTotal} · cliques no botão "Pedir ajuda da Claire" + escalations.
                  </span>
                </header>
                {events.length === 0 ? (
                  <div className="px-4 py-10 text-center text-[#9CA3AF] italic text-[12.5px]">
                    Sem eventos na janela.
                  </div>
                ) : (
                  <div className="p-3 flex flex-wrap gap-2">
                    {events.map((e) => {
                      const icon = e._id === "help_button_click"   ? <MousePointerClick className="w-3.5 h-3.5" />
                                  : e._id === "whatsapp_escalation" ? <Phone className="w-3.5 h-3.5" />
                                  :                                   <ExternalLink className="w-3.5 h-3.5" />;
                      return (
                        <span key={e._id || "unknown"}
                              data-testid={`claire-event-${e._id || "unknown"}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F3F4F6] border border-[#E5E7EB] text-[11.5px] font-semibold text-[#374151]">
                          {icon}
                          {e._id || "?"} · {e.count}
                        </span>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </MasterLayout>
  );
}
